import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { itemList, breadcrumbList } from "../lib/schema";
import SafetyBanner from "../components/SafetyBanner";
import { MapPin, ArrowRight } from "lucide-react";

/** Split a description into paragraphs and return the first `n` as one HTML block
 *  plus a flag telling us whether more content was truncated. */
function excerptParagraphs(text, n = 3) {
  if (!text) return { html: null, truncated: false };
  const paragraphs = text
    .split(/\n{2,}|\r\n\r\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const shown = paragraphs.slice(0, n);
  return { paragraphs: shown, truncated: paragraphs.length > n };
}

export default function SeoCategory() {
  const { kind, slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/category/${kind}/${slug}`);
        setData(r.data);
      } catch {
        setData({ error: true });
      }
    })();
  }, [kind, slug]);

  useSEO(
    data?.name
      ? {
          title: data.meta_title,
          description: data.meta_description,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: data.meta_title,
              description: data.meta_description,
              mainEntity: { "@type": "ItemList", numberOfItems: (data.items || []).length },
            },
            itemList(data.meta_title, data.items || []),
            breadcrumbList([
              { name: "Home", path: "/" },
              { name: "Best waterfalls", path: "/best-waterfalls" },
              { name: data.name, path: `/best-waterfalls-in/${kind}/${slug}` },
            ]),
          ],
        }
      : { title: "Best Waterfalls — WaterfallFinder" }
  );

  if (!data) return <main className="pt-16 max-w-7xl mx-auto p-8">Loading…</main>;
  if (data.error) return <main className="pt-16 max-w-7xl mx-auto p-8">Category not found.</main>;

  const items = data.items || [];

  return (
    <main className="pt-16" data-testid="seo-category">
      {/* Header */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold">
          {kind} guide
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight">
          The best waterfalls in <span className="heading-underline">{data.name}</span>
        </h1>
        <p className="mt-5 max-w-3xl text-lg text-stone-700 leading-relaxed">
          A hand-picked guide to {data.count} of the most stunning waterfalls in {data.name}.
          Each entry below has our personal notes, photos, driving directions and the
          practical detail you need to plan a visit — from parking and hike lengths to
          swimming rules and the best time of year to go.
        </p>
      </div>

      {/* Table of contents */}
      {items.length > 0 && (
        <nav
          aria-label="On this page"
          className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 mb-4"
          data-testid="category-toc"
        >
          <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
            <h2 className="text-sm font-bold text-stone-900 uppercase tracking-widest">
              On this page
            </h2>
            <ol className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm text-stone-700 list-decimal list-inside">
              {items.map((w) => (
                <li key={w.id}>
                  <a href={`#wf-${w.slug}`} className="hover:text-stone-900 hover:underline">
                    {w.name}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </nav>
      )}

      {/* Rich, per-waterfall long-form sections */}
      <section
        className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-16"
        data-testid="category-longform"
      >
        {items.map((w, i) => (
          <WaterfallSection key={w.id} wf={w} index={i + 1} />
        ))}
      </section>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-24">
        <SafetyBanner />
      </div>
    </main>
  );
}

/** Individual long-form waterfall section: heading, meta, image gallery, first
 *  ~3 paragraphs of the personal description, then a "Read more" CTA back to
 *  the full waterfall page. */
function WaterfallSection({ wf, index }) {
  const detailUrl = `/waterfalls/${wf.slug}`;
  const { paragraphs = [], truncated } = excerptParagraphs(wf.description, 3);
  const images = (wf.image_urls || []).slice(0, 3);
  const locationParts = [wf.city, wf.state, wf.country].filter(Boolean);

  return (
    <article
      id={`wf-${wf.slug}`}
      className="scroll-mt-24 border-t border-stone-200 pt-14 first:border-0 first:pt-0"
      data-testid={`wf-section-${wf.slug}`}
    >
      <header className="mb-6">
        <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold">
          #{index} · {wf.height_m ? `${wf.height_m} m drop` : "Featured waterfall"}
        </p>
        <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900">
          <Link
            to={detailUrl}
            className="hover:underline underline-offset-4"
            data-testid={`wf-section-title-${wf.slug}`}
          >
            {wf.name}
          </Link>
        </h2>
        {locationParts.length > 0 && (
          <p className="mt-2 text-sm text-stone-500 flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            {locationParts.join(" · ")}
          </p>
        )}
      </header>

      {/* Multi-image gallery — up to 3 side-by-side, mirrors the reference design */}
      {images.length > 0 && (
        <div
          className={`grid gap-2 mb-8 ${
            images.length === 1
              ? "grid-cols-1"
              : images.length === 2
              ? "grid-cols-2"
              : "grid-cols-3"
          }`}
          data-testid={`wf-section-gallery-${wf.slug}`}
        >
          {images.map((src, i) => (
            <Link
              key={i}
              to={detailUrl}
              className="block overflow-hidden rounded-xl bg-stone-100 aspect-[3/4]"
            >
              <img
                src={src}
                alt={`${wf.name} — ${i === 0 ? "hero" : "photo " + (i + 1)}`}
                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-300"
                loading="lazy"
              />
            </Link>
          ))}
        </div>
      )}

      {/* First few paragraphs of the reviewer's personal description */}
      {paragraphs.length > 0 ? (
        <div className="prose prose-stone max-w-none">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-stone-800 leading-relaxed text-[17px] mb-5">
              {p}
            </p>
          ))}
        </div>
      ) : wf.short_blurb ? (
        <p className="text-stone-800 leading-relaxed text-[17px]">{wf.short_blurb}</p>
      ) : null}

      <div className="mt-6">
        <Link
          to={detailUrl}
          data-testid={`wf-section-readmore-${wf.slug}`}
          className="inline-flex items-center gap-2 font-semibold text-stone-900 hover:opacity-80"
        >
          {truncated ? `Read the full guide to ${wf.name}` : `See maps, safety & photos for ${wf.name}`}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
}
